import React, { useState, useEffect, useMemo } from 'react';
import { Box, List, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { ProductCardList } from './ProductCardList';
import { useGetProductsByAttributeQuery } from '@headless-shell/store';
import PropTypes from 'prop-types';
import {
  DropdownBackdrop,
  DropdownContainer,
  Sidebar,
  CategoryRow,
  CategoryItem,
  MainContent,
  CategoryArrow,
  ProductsGrid,
  CategoryHeading,
  ActionsRow,
  CategoryDescriptionText
} from './styles';
import { events } from '../../constants';
import { SdfBusyIndicator, SdfButton } from '@waypoint/react-components';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveTab } from '@headless-shell/store';
import {
  getSafeId,
  getMarketplaceListingUrl,
  getCategoriesAndFacetType,
  getTranslatedCategories
} from '../../utils/helper';
import { colors, breakpoints } from '@headless-shell/shared-styles';
import { useTranslatedObject } from '../../utils/translation';
import {
  HeaderSubmenuCategoryCtaClick,
  HeaderSubmenuClick,
  sendGA4Data
} from '../../analytics/events';
import { useCategoriesData } from './categories';
import { buildSearchUrl } from '@headless-shell/shared-components';

export const MarketplaceDropdown = ({ solutions, navigate, locale = 'en-US', authenticated, isMarketplaceUser = false }) => {
  const dispatch = useDispatch();
  const activeTab = useSelector(state => state.app.activeTab);
  const isMediumScreen = useMediaQuery(
    `(min-width: ${breakpoints.md}) and (max-width: ${breakpoints.desktopWide})`
  );
  const header = useTranslatedObject('header');
  const page = useTranslatedObject('page');
  const solutions_facetType = useTranslatedObject('solutions_facetType');
  const industry_facetType = useTranslatedObject('industry_facetType');
  const solutionCategories = useTranslatedObject('solution_categories');
  const industryCategories = useTranslatedObject('industry_categories');
  const type = 'solutions';
  const facetName = type === 'solutions' ? 'Solutions' : 'Industry';
  const { categoriesData, isError } = useCategoriesData(facetName);

  const { facetType, pageKey } = getCategoriesAndFacetType(
    type,
    solutions_facetType,
    industry_facetType
  );

  const categories = getTranslatedCategories(
    categoriesData,
    type,
    solutionCategories,
    industryCategories
  );
  const firstCategory = categories?.[0] || { label: 'Recruiting & Onboarding' };
  const [activeCategory, setActiveCategory] = useState(firstCategory.label);
  const open = activeTab === solutions;

  // Get the attribute ID for the active category
  const activeCategoryData = categories.find(cat => cat.label === activeCategory);
  const activeAttributeId = activeCategoryData?.attributeId;

  const productCount = isMediumScreen ? 2 : 3;

  // Fetch products for the selected category using RTK Query
  const {
    currentData: productsData,
    isFetching: productsFetching,
    isError: productsError
  } = useGetProductsByAttributeQuery(
    { attributeId: activeAttributeId, count: productCount },
    {
      skip: !activeAttributeId || !open // Only fetch if we have an ID and dropdown is open
    }
  );

  // Transform API products to component format
  const products = useMemo(() => {
    if (productsError || !productsData?.products) {
      return [];
    }

    return productsData.products.map(product => ({
      id: product._id || product.id,
      name: product.name,
      company: product.vendorName || product.provider?.name,
      description: product.overview?.splashDescription || '',
      targetUrl:
        product?.url?.href
        || product?.url
        || product?.href
        || product?.listing?.href
        || product?.listing?.url
        || product?.sr_sv_href_s
        || product?.sr_sv_appurl_s
        || '',
      rating: parseFloat(product.absoluteRating) || 0,
      reviews: product.numRatings || '0',
      category: activeCategory,
      logo: product.listing?.myAppLogoIconUrl || product.provider?.name?.[0] || '?',
      logoColor: colors.MYAPPGRAY.light,
      bestseller: !!product.productRibbon?.text,
    }));
  }, [productsData, productsError, activeCategory]);

  useEffect(() => {
    if (open) {
      setActiveCategory(firstCategory.label);
    }
  }, [open, firstCategory.label]);

  const description = activeCategoryData?.description || '';

  const handleClose = () => {
    dispatch(setActiveTab(''));
  };

  const onCategoryClick = (categoryLabel) => {
    setActiveCategory(categoryLabel);

    sendGA4Data(HeaderSubmenuClick, {
      customEventName: `${events.EVENT_NAMES.HEADER_SUBMENU}_${activeTab}`,
      eventCategory: activeTab,
      eventLabel: categoryLabel
    });
  };

  if (!open) return null;

  // If API failed, don't show the dropdown at all
  if (isError) {
    console.error('Product tags API failed - Solutions dropdown disabled');
    return null;
  }

  const handleCategoryCTAClick = (category, ctaName) => {
    const hasAttribute = !!category.attributeId;
    const isViewMoreApps = ctaName === "View more apps";
    const isHRIS = category.label === 'HRIS';
    let pageName = 'listing';
    if (!isViewMoreApps && (hasAttribute || category?.overviewLabel)) {
          pageName = page?.[pageKey] || 'listing';
    }
    const url = getMarketplaceListingUrl({
      locale,
      facetType: isViewMoreApps ? facetType : '',
      solutionName: isViewMoreApps ? category.key : '',
      attributeId: isViewMoreApps ? (isHRIS ? category.attributeId : '') : category.attributeId,
      page: pageName,
      apicLabel: (!isViewMoreApps && category?.overviewLabel) || '',
    });
    const [basePath = '/listing', hashFragment = ''] = url.split('#');
    const targetUrl = pageName === 'listing'
      ? buildSearchUrl({
          searchValue: '',
          hashParam: hashFragment ? `#${hashFragment}` : '',
          isAuthenticated: authenticated,
          basePath: basePath.startsWith('/') ? basePath : `/${basePath}`
        })
      : url;
    sendGA4Data(HeaderSubmenuCategoryCtaClick, {
      customEventName: `${events.EVENT_NAMES.HEADER_SUBMENU}_${activeTab}`,
      eventCategory: category.label,
      eventLabel: ctaName
    });
    handleClose();
    navigate(targetUrl);
  };

  return (
    <React.Fragment>
      <DropdownBackdrop
        id="marketplace-dropdown-backdrop"
        open={open}
        onClick={handleClose}
        loggedIn={authenticated}
      />
      <DropdownContainer id="marketplace-dropdown" loggedIn={authenticated}>
        <Sidebar id="marketplace-sidebar">
          <List disablePadding>
          {categories.map((category) => {
            const isActive = activeCategory === category.label;

            return (
              <CategoryRow key={category.label}>
                <CategoryItem
                  id={getSafeId('marketplace-category', category.label)}
                  selected={isActive}
                  onClick={() => onCategoryClick(category.label)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <ListItemText primary={category.label} />
                  </Box>

                  {isActive && <CategoryArrow component="span">›</CategoryArrow>}
                </CategoryItem>
              </CategoryRow>
            );
          })}
          </List>
        </Sidebar>

        <MainContent id="marketplace-main-content">
          <Box mb={3}>
            <CategoryHeading>
              {activeCategoryData?.label || activeCategory}
            </CategoryHeading>
            <CategoryDescriptionText>
              {description}
            </CategoryDescriptionText>
          </Box>

          {productsFetching && !productsData ? (
            <Box p={3}>
              <SdfBusyIndicator />
            </Box>
          ) : (
            <>
              {!isMarketplaceUser && (
              <ProductsGrid id="marketplace-products-grid">
                <ProductCardList products={products} navigate={navigate} />
              </ProductsGrid>
              )}

              <ActionsRow id="marketplace-actions" className={isMarketplaceUser ? 'mt-64' : ''}>
                {!activeCategoryData?.hideOverview && header.overview && (
                  <SdfButton
                    id="marketplace-overview-button"
                    emphasis="primary"
                    variant="contained"
                    onClick={() => handleCategoryCTAClick(activeCategoryData, "Overview")}
                  >
                    {activeCategoryData?.overviewLabel || activeCategoryData?.label || activeCategory} {header.overview}
                  </SdfButton>
                )}
                <SdfButton
                  id="marketplace-view-more-button"
                  variant="contained"
                  onClick={() => handleCategoryCTAClick(activeCategoryData, "View more apps")}
                >
                  {header.view_more_apps}
                </SdfButton>
              </ActionsRow>
            </>
          )}
        </MainContent>
      </DropdownContainer>
    </React.Fragment>
  );
};

MarketplaceDropdown.propTypes = {
  onLoadingChange: PropTypes.func,
  solutions: PropTypes.string,
  navigate: PropTypes.func,
  locale: PropTypes.string,
  isMarketplaceUser: PropTypes.bool
};